/**
 *
 * Provides fake authentication over Cognito (Amplify v6)
 */

import Validation from '~/util/Validation';
import {
  INVALID_PARAMETER,
  INVALID_PARAMETER_FORGOT_PASS,
  USER_DOES_NOT_EXIST,
  INCORRECT_USERNAME_OR_PASSWORD,
  USER_UNCONFIRMED,
  USERNAME_EXISTS,
  REGISTRATION_OK,
  LOGIN_OK,
  VERIFICATION_NON_EXISTENT_USER,
  VERIFICATION_INVALID_TOKEN,
  VALIDATION_ALREADY_VALIDATED,
  NEW_PASSWORD_REQUIRED,
  USER_NOT_ACTIVATED,
  EXPIRED_PASSWORD,
} from './CognitoResponse';
import type { AuthSession, AuthUser } from 'aws-amplify/auth';

const DEFAULT_AUTH_DELAY = 50;

export const delayedResolve = (
  value?: any,
  delay: number = DEFAULT_AUTH_DELAY,
): Promise<any> =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), delay);
  });

export const delayedReject = (
  value?: any,
  delay: number = DEFAULT_AUTH_DELAY,
): Promise<any> =>
  new Promise((_, reject) => {
    setTimeout(() => reject(value), delay);
  });

type UserType = {
  email: string;
  password: string;
  token: number;
  name?: string;
  phone?: string;
};

export const USER: {
  [key: string]: UserType;
} = {
  UNCONFIRMED: {
    email: 'unconfirmed@bndl.nl',
    password: 'Pass%1234567',
    token: 123723,
    name: 'Philipp Beau',
    phone: '3806685012345',
  },
  NON_EXISTENT: {
    email: 'non-existent-user_mock@bndl.nl',
    password: 'Pass%1234567',
    token: 123623,
    name: 'Philipp Beau',
    phone: '3806685012345',
  },
  VALID: {
    email: 'rob@bndl.nl',
    password: 'Pass%1234567',
    token: 125123,
    name: 'Rob van Kruijsdijk',
    phone: '3806685012345',
  },
  INVITED_USER: {
    email: 'inviteduser@bndl.nl',
    password: 'Pass!1234567',
    token: 125123,
    name: 'Rob van Kruijsdijk',
    phone: '3806685012345',
  },
  EXPIRED_USER: {
    email: 'expired_user@bndl.nl',
    password: 'Pass!1234567',
    token: 125123,
    name: 'Ramon Gebben',
    phone: '3806685012345',
  },
};

const FakeUser: AuthUser = {
  username: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
  userId: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
  signInDetails: {
    loginId: 'philipp@bndl.nl',
    authFlowType: 'USER_SRP_AUTH',
  },
};

const FakeSession: AuthSession = {
  tokens: {
    accessToken: {
      toString: () => 'DEV_JWT_TOKEN',
      payload: {
        sub: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
        aud: '4bijda6vkc8qubfke287jokv18',
        'custom:phone': '0683830330',
        email_verified: true,
        token_use: 'access',
        auth_time: 1620900861,
        iss: 'https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_G4pGMV682',
        name: 'Philipp Beau',
        'cognito:username': '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
        exp: 1622798662,
        iat: 1622795062,
        email: 'philipp@bndl.nl',
      },
    },
  },
};

const FakeUserAttributes = {
  sub: '4e8b7741-cb9a-432f-8e92-9bfba0e4d1ad',
  name: 'Bruce Banner',
  email: 'angrygreenman@gmail.com',
  'custom:phone': '0683830330',
  email_verified: 'true',
  // Additional attributes for testing
  offices: [
    {
      id: 'office-1',
      name: 'Amsterdam',
    },
    {
      id: 'office-2',
      name: 'Utrecht',
    },
  ],
  users: [
    {
      id: 'u---user-1',
      name: 'Bruce Banner',
    },
    {
      id: 'u---user-2',
      name: 'Peter Parker',
    },
    {
      id: 'u---user-3',
      name: 'Matthew Murdock',
    },
  ],
};

/** Amplify v6 Auth functions */
const Auth = {
  _loggedIn: !!sessionStorage.getItem('dhAuth'),

  getCurrentUser: (): Promise<AuthUser> => {
    if (!Auth._loggedIn) {
      return delayedReject('User is not authenticated');
    }
    return delayedResolve(FakeUser, 0);
  },

  fetchAuthSession: (): Promise<any> => {
    if (!Auth._loggedIn) {
      return delayedReject('No current session');
    }
    return delayedResolve(FakeSession, 0);
  },

  fetchUserAttributes: (): Promise<any> => {
    if (!Auth._loggedIn) {
      return delayedReject('User is not authenticated');
    }
    return delayedResolve(FakeUserAttributes, 0);
  },

  signIn: ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }): Promise<any> => {
    if (username === USER.NON_EXISTENT.email) {
      return delayedReject({
        name: 'UserNotFoundException',
        ...USER_DOES_NOT_EXIST,
      });
    }

    if (username === USER.UNCONFIRMED.email) {
      return delayedReject({
        name: 'UserNotConfirmedException',
        ...USER_UNCONFIRMED,
      });
    }

    if (username === USER.VALID.email) {
      if (password !== USER.VALID.password) {
        return delayedReject({
          name: 'NotAuthorizedException',
          ...INCORRECT_USERNAME_OR_PASSWORD,
        });
      }

      Auth._loggedIn = true;
      sessionStorage.setItem('dhAuth', 'loggedIn');
      return delayedResolve({
        isSignedIn: true,
        nextStep: { signInStep: 'DONE' },
        ...LOGIN_OK,
      });
    }

    if (
      username === USER.EXPIRED_USER.email &&
      password === USER.EXPIRED_USER.password
    ) {
      return delayedReject({
        name: 'PasswordResetRequiredException',
        ...EXPIRED_PASSWORD,
      });
    }

    if (
      username === USER.INVITED_USER.email &&
      password === USER.INVITED_USER.password
    ) {
      return delayedResolve({
        isSignedIn: false,
        nextStep: {
          signInStep: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
          missingAttributes: [],
        },
        ...NEW_PASSWORD_REQUIRED,
      });
    }

    return delayedReject({
      name: 'UserNotFoundException',
      ...USER_DOES_NOT_EXIST,
    });
  },

  signUp: ({
    username,
    password,
    options,
  }: {
    username: string;
    password: string;
    options?: {
      userAttributes?: Record<string, string>;
      autoSignIn?: boolean;
    };
  }): Promise<any> => {
    if (!Validation.Email.isValid(username)) {
      return delayedReject(INVALID_PARAMETER);
    }

    if (!Validation.Password.isValid(password)) {
      return delayedReject(INVALID_PARAMETER);
    }

    if (
      options?.userAttributes?.name == null ||
      !Validation.String.isString(options.userAttributes.name)
    ) {
      return delayedReject(INVALID_PARAMETER);
    }

    if (username === 'rob@bndl.nl') {
      return delayedReject(USERNAME_EXISTS);
    }

    return delayedResolve({
      isSignUpComplete: false,
      nextStep: {
        signUpStep: 'CONFIRM_SIGN_UP',
        codeDeliveryDetails: {
          destination: username,
          deliveryMedium: 'EMAIL',
        },
      },
      userId: 'fake-user-id',
      ...REGISTRATION_OK,
    });
  },

  confirmSignUp: ({
    username,
    confirmationCode,
  }: {
    username: string;
    confirmationCode: string;
  }): Promise<any> => {
    if (username === USER.VALID.email) {
      return delayedReject({
        name: 'AliasExistsException',
        ...VALIDATION_ALREADY_VALIDATED,
      });
    }

    if (username === USER.NON_EXISTENT.email) {
      return delayedReject({
        name: 'UserNotFoundException',
        ...VERIFICATION_NON_EXISTENT_USER,
      });
    }

    if (username === USER.UNCONFIRMED.email) {
      if (parseInt(confirmationCode) !== USER.UNCONFIRMED.token) {
        return delayedReject({
          name: 'CodeMismatchException',
          ...VERIFICATION_INVALID_TOKEN,
        });
      }

      return delayedResolve({
        isSignUpComplete: true,
        nextStep: { signUpStep: 'DONE' },
      });
    }

    return delayedReject({
      name: 'UserNotFoundException',
      ...VERIFICATION_NON_EXISTENT_USER,
    });
  },

  resendSignUpCode: ({ username }: { username: string }): Promise<any> =>
    delayedResolve({
      destination: username,
      deliveryMedium: 'EMAIL',
      attributeName: 'email',
    }),

  resetPassword: ({ username }: { username: string }): Promise<any> => {
    if (username === USER.NON_EXISTENT.email) {
      return delayedReject({
        name: 'UserNotFoundException',
        ...USER_DOES_NOT_EXIST,
      });
    }

    if (username === USER.UNCONFIRMED.email) {
      return delayedReject(INVALID_PARAMETER_FORGOT_PASS);
    }

    if (username === USER.INVITED_USER.email) {
      return delayedReject({
        name: 'UserNotConfirmedException',
        ...USER_NOT_ACTIVATED,
      });
    }

    return delayedResolve({
      isPasswordReset: false,
      nextStep: {
        resetPasswordStep: 'CONFIRM_RESET_PASSWORD_WITH_CODE',
        codeDeliveryDetails: {
          destination: username,
          deliveryMedium: 'EMAIL',
        },
      },
    });
  },

  confirmResetPassword: ({
    username,
    confirmationCode,
    newPassword,
  }: {
    username: string;
    confirmationCode: string;
    newPassword: string;
  }): Promise<any> => {
    if (confirmationCode !== USER.VALID.token.toString()) {
      return delayedReject({
        name: 'CodeMismatchException',
        ...VERIFICATION_INVALID_TOKEN,
      });
    }

    const result = username + confirmationCode + newPassword;
    return delayedResolve({
      isPasswordReset: true,
      nextStep: { resetPasswordStep: 'DONE' },
      result,
    });
  },

  confirmSignIn: ({
    challengeResponse,
    options,
  }: {
    challengeResponse: string;
    options?: any;
  }): Promise<any> => {
    if (challengeResponse && options) {
      Auth._loggedIn = true;
      sessionStorage.setItem('dhAuth', 'loggedIn');
      return delayedResolve({
        isSignedIn: true,
        nextStep: { signInStep: 'DONE' },
        ...LOGIN_OK,
      });
    } else {
      return delayedReject(INVALID_PARAMETER);
    }
  },

  updatePassword: ({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }): Promise<any> => {
    if (!Auth._loggedIn) {
      return delayedReject({
        name: 'NotAuthorizedException',
        message: 'User is not authenticated',
      });
    }

    if (USER.VALID.password === oldPassword && newPassword) {
      return delayedResolve({ message: 'Password updated successfully' });
    } else {
      return delayedReject(INVALID_PARAMETER);
    }
  },

  signOut: (): Promise<any> => {
    Auth._loggedIn = false;
    sessionStorage.removeItem('dhAuth');
    return delayedResolve({ isSignedOut: true });
  },
};

export default Auth;
